
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Upload, Camera, Image as ImageIcon, Video, AlertTriangle, Sparkles, Home as HomeIcon, MapPin, Phone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { usePets } from '@/hooks/usePets';
import { createPost } from '@/services/postsService';
import { supabase } from '@/integrations/supabase/client';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Camera as CapCamera, CameraResultType, CameraSource } from '@capacitor/camera';
import { Capacitor } from '@capacitor/core';
import { useSubscription } from '@/hooks/useSubscription';
import { canUploadMedia } from '@/services/subscriptionService';
import UpgradeModal from '@/components/UpgradeModal';
import StorageProgressBar from '@/components/StorageProgressBar';

type PostFlag = 'memory' | 'milestone' | 'lost' | 'adoption';

const FLAG_TO_TYPE: Record<PostFlag, string> = {
  memory: 'memory',
  milestone: 'milestone',
  lost: 'lost_pet',
  adoption: 'adoption_post',
};

const CreatePost = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isUploading, setIsUploading] = useState(false);
  const [files, setFiles] = useState<FileList | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [caption, setCaption] = useState('');
  const [selectedPetId, setSelectedPetId] = useState<string>('');
  const [privacySetting, setPrivacySetting] = useState<'public' | 'private' | 'followers_only'>('public');
  const [postFlag, setPostFlag] = useState<PostFlag>('memory');

  // Lost Pet specific
  const [contactPhone, setContactPhone] = useState('');
  const [lostAddress, setLostAddress] = useState('');
  const [lostLat, setLostLat] = useState<number | null>(null);
  const [lostLng, setLostLng] = useState<number | null>(null);
  const [geoLoading, setGeoLoading] = useState(false);

  // Pick from existing feed
  const [feedPickerOpen, setFeedPickerOpen] = useState(false);
  const [feedMedia, setFeedMedia] = useState<Array<{ id: string; url: string; media_type: string }>>([]);
  const [feedLoading, setFeedLoading] = useState(false);
  const [importingFeedMedia, setImportingFeedMedia] = useState(false);

  const { pets, isLoading: petsLoading } = usePets();
  const { subscription, quota } = useSubscription();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);

  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeReason, setUpgradeReason] = useState<'storage'>('storage');

  useEffect(() => {
    const state = location.state as { selectedPetId?: string; postFlag?: PostFlag };
    if (state?.selectedPetId) setSelectedPetId(state.selectedPetId);
    if (state?.postFlag && ['memory','milestone','lost','adoption'].includes(state.postFlag)) {
      setPostFlag(state.postFlag);
    }
  }, [location.state]);

  const useCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast.error(t('createPost.lost.geoUnsupported', 'Geolocation not supported on this device'));
      return;
    }
    setGeoLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLostLat(pos.coords.latitude);
        setLostLng(pos.coords.longitude);
        setGeoLoading(false);
        toast.success(t('createPost.lost.geoCaptured', 'Location captured'));
      },
      () => {
        setGeoLoading(false);
        toast.error(t('createPost.lost.geoDenied', 'Could not get current location'));
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const openFeedPicker = async () => {
    if (!selectedPetId) {
      toast.error(t('modals.post.pleaseSelectPet'));
      return;
    }
    setFeedPickerOpen(true);
    setFeedLoading(true);
    try {
      const { data, error } = await supabase
        .from('memory_posts')
        .select('id, media:media_files(id, url, media_type)')
        .eq('pet_id', selectedPetId)
        .order('created_at', { ascending: false })
        .limit(60);
      if (error) throw error;
      const flat: Array<{ id: string; url: string; media_type: string }> = [];
      (data || []).forEach((p: any) => {
        (p.media || []).forEach((m: any) => {
          if (m?.url && typeof m.media_type === 'string' && m.media_type.startsWith('image')) flat.push(m);
        });
      });
      setFeedMedia(flat);
    } catch (err: any) {
      console.error('Error loading feed media:', err);
      toast.error(t('createPost.feedPicker.loadError', 'Could not load your feed photos'));
    } finally {
      setFeedLoading(false);
    }
  };

  const importFromFeed = async (mediaUrl: string) => {
    try {
      setImportingFeedMedia(true);
      const resp = await fetch(mediaUrl);
      if (!resp.ok) throw new Error('fetch failed');
      const blob = await resp.blob();
      const ext = (blob.type.split('/')[1] || 'jpg').split(';')[0];
      const file = new File([blob], `feed_${Date.now()}.${ext}`, { type: blob.type || 'image/jpeg' });
      const dt = new DataTransfer();
      dt.items.add(file);
      handleFileChange(dt.files);
      setFeedPickerOpen(false);
      toast.success(t('createPost.feedPicker.imported', 'Photo imported from your feed'));
    } catch (err) {
      console.error('Error importing feed photo:', err);
      toast.error(t('createPost.feedPicker.importError', 'Could not import this photo'));
    } finally {
      setImportingFeedMedia(false);
    }
  };

  const handleFileChange = (files: FileList | null) => {
    console.log("File change detected:", files);
    if (!files || files.length === 0) {
      setFiles(null);
      setPreview(null);
      return;
    }

    setFiles(files);
    
    const file = files[0];
    const fileUrl = URL.createObjectURL(file);
    setPreview(fileUrl);
    
    return () => {
      URL.revokeObjectURL(fileUrl);
    };
  };

  const handleUploadAreaClick = () => {
    console.log("Upload area clicked");
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Native camera functions
  const takePhoto = async () => {
    try {
      const image = await CapCamera.getPhoto({
        quality: 90,
        allowEditing: true,
        resultType: CameraResultType.Uri,
        source: CameraSource.Camera
      });
      
      const response = await fetch(image.webPath!);
      const blob = await response.blob();
      const file = new File([blob], `photo_${Date.now()}.${image.format}`, {
        type: `image/${image.format}`
      });
      
      const dataTransfer = new DataTransfer();
      dataTransfer.items.add(file);
      handleFileChange(dataTransfer.files);
      
      toast.success(t('modals.post.photoTaken'));
    } catch (error: any) {
      if (error.message !== 'User cancelled photos app') {
        console.error('Error taking photo:', error);
        toast.error(t('modals.post.photoError'));
      }
    }
  };

  const pickFromGallery = async () => {
    try {
      const images = await CapCamera.pickImages({
        quality: 90,
        limit: 10 // Allow multiple selection
      });
      
      if (!images.photos || images.photos.length === 0) {
        return;
      }

      const dataTransfer = new DataTransfer();
      
      for (const photo of images.photos) {
        const response = await fetch(photo.webPath);
        const blob = await response.blob();
        const file = new File([blob], `gallery_${Date.now()}_${Math.random()}.${photo.format}`, {
          type: `image/${photo.format}`
        });
        dataTransfer.items.add(file);
      }
      
      handleFileChange(dataTransfer.files);
      toast.success(t('modals.post.gallerySelected', { count: images.photos.length }));
    } catch (error: any) {
      if (error.message !== 'User cancelled photos app') {
        console.error('Error picking from gallery:', error);
      toast.error(t('modals.post.galleryError'));
      }
    }
  };

  const recordVideo = () => {
    if (videoInputRef.current) {
      videoInputRef.current.click();
    }
  };

  const isNativePlatform = Capacitor.isNativePlatform();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!files || files.length === 0) {
      toast.error(t('modals.post.pleaseSelectFile'));
      return;
    }
    
    if (!selectedPetId) {
      toast.error(t('modals.post.pleaseSelectPet'));
      return;
    }
    
    // ✅ FASE 3: Check storage limit before uploading
    const file = files[0];
    const storageCheck = await canUploadMedia(file.size);
    if (!storageCheck.canUpload) {
      console.log('🚫 Storage limit reached, showing upgrade modal');
      setUpgradeReason('storage');
      setShowUpgradeModal(true);
      return;
    }

    setIsUploading(true);
    
    try {
      const mediaFiles = Array.from(files);

      if (postFlag === 'lost') {
        if (!contactPhone.trim()) {
          toast.error(t('createPost.lost.phoneRequired', 'Please enter a contact phone'));
          setIsUploading(false);
          return;
        }
      }

      const formData: any = {
        petId: selectedPetId,
        mediaFiles,
        privacySetting,
        post_type: FLAG_TO_TYPE[postFlag],
      };
      if (caption) formData.caption = caption;
      if (postFlag === 'lost') {
        formData.contact_phone = contactPhone.trim();
        formData.lost_address = lostAddress.trim() || null;
        formData.lost_lat = lostLat;
        formData.lost_lng = lostLng;
        formData.lost_last_seen_at = new Date().toISOString();
      }

      await createPost(formData);

      toast.success(t('modals.post.memoryPostedSuccess'));

      if (postFlag === 'lost' || postFlag === 'adoption') {
        navigate('/community');
      } else if (location.state?.selectedPetId) {
        navigate(`/pet/${selectedPetId}`);
      } else {
        navigate('/feed');
      }
    } catch (error: any) {
      console.error('Error creating post:', error);
      toast.error(error.message || 'Failed to create post. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const flagBanner = (() => {
    if (postFlag === 'lost') {
      return {
        icon: AlertTriangle,
        title: t('postTypes.lostPet.banner', 'Lost Pet Alert'),
        desc: t('createPost.lost.desc', 'Help neighbors find your pet quickly.'),
        tone: 'from-red-600 to-orange-600',
      };
    }
    if (postFlag === 'milestone') {
      return {
        icon: Sparkles,
        title: t('postTypes.milestone.banner', 'Milestone'),
        desc: t('createPost.milestone.desc', 'Celebrate a special moment.'),
        tone: 'from-amber-500 to-orange-500',
      };
    }
    if (postFlag === 'adoption') {
      return {
        icon: HomeIcon,
        title: t('postTypes.adoption.banner', 'Available for Adoption'),
        desc: t('createPost.adoption.desc', 'Share this pet to find their forever home.'),
        tone: 'from-teal-500 to-cyan-600',
      };
    }
    return null;
  })();

  const selectedPet = pets.find(p => p.id === selectedPetId);

  return (
    <div className="p-4 pb-24">
      {/* ✅ FASE 3: Storage Progress Bar - always visible */}
      {quota && (
        <div className="mb-6">
          <StorageProgressBar
            used={quota.storage.used}
            total={quota.storage.total}
            showUpgradeButton={true}
            compact={false}
          />
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="space-y-6">
        {flagBanner && (
          <div className={`rounded-2xl p-4 text-white shadow-md bg-gradient-to-r ${flagBanner.tone}`}>
            <div className="flex items-center gap-2">
              <flagBanner.icon className="h-5 w-5" />
              <span className="font-bold uppercase tracking-wide text-sm">{flagBanner.title}</span>
            </div>
            <p className="text-white/90 text-xs mt-1">{flagBanner.desc}</p>
          </div>
        )}

        <div className="space-y-2">
          <Label htmlFor="pet-select">{t('modals.post.selectPet')}</Label>
          <Select
            value={selectedPetId}
            onValueChange={setSelectedPetId}
            required
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder={t('modals.post.choosePet')} />
            </SelectTrigger>
            <SelectContent>
              {petsLoading ? (
                <SelectItem value="loading" disabled>
                  {t('modals.post.loadingPets')}
                </SelectItem>
              ) : pets.length > 0 ? (
                pets.map((pet) => (
                  <SelectItem key={pet.id} value={pet.id} className="flex items-center">
                    <div className="flex items-center">
                      <Avatar className="h-6 w-6 mr-2">
                        {pet.photo_url ? (
                          <AvatarImage src={pet.photo_url} alt={pet.name} />
                        ) : (
                          <AvatarFallback>{pet.name.charAt(0)}</AvatarFallback>
                        )}
                      </Avatar>
                      {pet.name}
                    </div>
                  </SelectItem>
                ))
              ) : (
                <SelectItem value="no-pets" disabled>
                  {t('modals.post.noPetsAvailable')}
                </SelectItem>
              )}
            </SelectContent>
          </Select>
          
          {pets.length === 0 && (
            <p className="text-sm text-amber-600 mt-1">
              {t('modals.post.addPetFirst')}{' '}
              <Button
                variant="link"
                className="p-0 h-auto text-blue-600"
                onClick={() => navigate('/my-pets')}
              >
                {t('modals.post.addPet')}
              </Button>
            </p>
          )}

          {selectedPet && (
            <div className="flex items-center mt-2">
              <Avatar className="h-8 w-8 mr-2">
                {selectedPet.photo_url ? (
                  <AvatarImage src={selectedPet.photo_url} alt={selectedPet.name} />
                ) : (
                  <AvatarFallback>{selectedPet.name.charAt(0)}</AvatarFallback>
                )}
              </Avatar>
              <span className="text-sm font-medium">{selectedPet.name}</span>
            </div>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="media-upload">{t('modals.post.photoVideo')}</Label>
          
          <div className="flex flex-col items-center">
            {preview ? (
              <div className="relative mb-4 w-full max-h-80 rounded-lg overflow-hidden">
                <img
                  src={preview}
                  alt="Preview"
                  className="w-full h-full object-contain"
                />
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  className="absolute top-2 right-2"
                  onClick={() => {
                    setFiles(null);
                    setPreview(null);
                  }}
                >
                  {t('modals.post.remove')}
                </Button>
              </div>
            ) : (
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 w-full mb-4 flex flex-col items-center">
                {isNativePlatform ? (
                  <>
                    <div className="flex flex-wrap gap-3 mb-4 justify-center">
                      <Button
                        type="button"
                        onClick={takePhoto}
                        variant="outline"
                        className="flex items-center gap-2"
                      >
                        <Camera className="h-5 w-5" />
                        {t('modals.post.takePhoto')}
                      </Button>
                      
                      <Button
                        type="button"
                        onClick={recordVideo}
                        variant="outline"
                        className="flex items-center gap-2"
                      >
                        <Video className="h-5 w-5" />
                        {t('modals.post.takeVideo')}
                      </Button>
                      
                      <Button
                        type="button"
                        onClick={pickFromGallery}
                        variant="outline"
                        className="flex items-center gap-2"
                      >
                        <ImageIcon className="h-5 w-5" />
                        {t('modals.post.pickFromGallery')}
                      </Button>
                      
                      <Button
                        type="button"
                        onClick={handleUploadAreaClick}
                        variant="outline"
                        className="flex items-center gap-2"
                      >
                        <Upload className="h-5 w-5" />
                        {t('modals.post.selectFile')}
                      </Button>
                    </div>
                    
                    <p className="text-gray-500 text-sm text-center">
                      {t('modals.post.orSelectFile')}
                    </p>
                  </>
                ) : (
                  <>
                    <div 
                      className="cursor-pointer"
                      onClick={handleUploadAreaClick}
                    >
                      <Camera className="h-12 w-12 text-gray-400 mb-2" />
                    </div>
                    <p className="text-gray-500 text-sm text-center">
                      {t('modals.post.uploadPrompt')}
                    </p>
                  </>
                )}
              </div>
            )}

            <input
              id="media-upload"
              type="file"
              className="hidden"
              accept="image/*,video/*"
              multiple={true}
              onChange={(e) => handleFileChange(e.target.files)}
              ref={fileInputRef}
            />
            
            <input
              id="video-capture"
              type="file"
              className="hidden"
              accept="video/*"
              capture="environment"
              onChange={(e) => handleFileChange(e.target.files)}
              ref={videoInputRef}
            />
            
            <p className="text-xs text-gray-500">
              {t('modals.post.uploadDescription')}
            </p>

            {(postFlag === 'lost' || postFlag === 'adoption') && !preview && (
              <div className="w-full mt-3 flex flex-col items-center">
                <div className="flex items-center gap-2 w-full">
                  <div className="flex-1 h-px bg-border" />
                  <span className="text-xs text-muted-foreground uppercase tracking-wide">
                    {t('common.or', 'or')}
                  </span>
                  <div className="flex-1 h-px bg-border" />
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={openFeedPicker}
                  disabled={!selectedPetId}
                  className="mt-3 flex items-center gap-2"
                >
                  <ImageIcon className="h-4 w-4" />
                  {t('createPost.feedPicker.button', "Use a photo from my pet's feed")}
                </Button>
                {!selectedPetId && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {t('createPost.feedPicker.selectPetFirst', 'Select a pet first to browse their photos')}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        <Dialog open={feedPickerOpen} onOpenChange={setFeedPickerOpen}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {t('createPost.feedPicker.title', "Choose a photo from your pet's feed")}
              </DialogTitle>
            </DialogHeader>
            {feedLoading ? (
              <div className="py-12 text-center text-sm text-muted-foreground">
                {t('common.loading', 'Loading...')}
              </div>
            ) : feedMedia.length === 0 ? (
              <div className="py-12 text-center text-sm text-muted-foreground">
                {t('createPost.feedPicker.empty', 'No photos found for this pet yet.')}
              </div>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                {feedMedia.map((m) => (
                  <button
                    type="button"
                    key={m.id}
                    onClick={() => importFromFeed(m.url)}
                    disabled={importingFeedMedia}
                    className="relative aspect-square overflow-hidden rounded-md border hover:ring-2 hover:ring-pet-purple transition disabled:opacity-50"
                  >
                    <img
                      src={m.url}
                      alt="Feed media"
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </button>
                ))}
              </div>
            )}
          </DialogContent>
        </Dialog>


        {postFlag === 'lost' && (
          <div className="space-y-3 rounded-xl border border-red-200 bg-red-50/60 p-4">
            <div className="space-y-1.5">
              <Label htmlFor="contact-phone" className="text-red-900">
                {t('createPost.lost.phoneLabel', 'Contact phone (WhatsApp enabled)')} *
              </Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-red-600" />
                <Input
                  id="contact-phone"
                  type="tel"
                  inputMode="tel"
                  required
                  placeholder="+57 300 000 0000"
                  value={contactPhone}
                  onChange={(e) => setContactPhone(e.target.value)}
                  className="pl-9"
                  maxLength={30}
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="lost-address" className="text-red-900">
                {t('createPost.lost.addressLabel', 'Last seen zone / address')}
              </Label>
              <Input
                id="lost-address"
                placeholder={t('createPost.lost.addressPlaceholder', 'e.g. Parque El Virrey, Bogotá')}
                value={lostAddress}
                onChange={(e) => setLostAddress(e.target.value)}
                maxLength={200}
              />
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={useCurrentLocation}
                disabled={geoLoading}
                className="border-red-300 text-red-700 hover:bg-red-100"
              >
                <MapPin className="h-4 w-4 mr-1.5" />
                {geoLoading
                  ? t('createPost.lost.geoLoading', 'Getting location...')
                  : t('createPost.lost.useCurrent', 'Use current location')}
              </Button>
              {lostLat != null && lostLng != null && (
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${lostLat},${lostLng}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-red-700 underline"
                >
                  {lostLat.toFixed(5)}, {lostLng.toFixed(5)} · {t('postTypes.lostPet.viewMap', 'View on map')}
                </a>
              )}
            </div>
          </div>
        )}

        <div className="space-y-2">
          <Label htmlFor="caption">{t('modals.post.caption')}</Label>
          <Textarea
            id="caption"
            placeholder={t('modals.post.captionPlaceholder')}
            className="h-24"
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
          />
        </div>

        <div className="space-y-2">
          <Label>{t('modals.post.privacy')}</Label>
          <RadioGroup
            value={privacySetting}
            onValueChange={(value: 'public' | 'private' | 'followers_only') =>
              setPrivacySetting(value)
            }
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="public" id="privacy-public" />
              <Label htmlFor="privacy-public">{t('modals.post.privacyPublic')}</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="followers_only" id="privacy-followers" />
              <Label htmlFor="privacy-followers">{t('modals.post.privacyFollowersOnly')}</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="private" id="privacy-private" />
              <Label htmlFor="privacy-private">{t('modals.post.privacyPrivate')}</Label>
            </div>
          </RadioGroup>
        </div>

        <Button
          type="submit"
          className="w-full bg-pet-purple hover:bg-pet-purple/90"
          disabled={isUploading || !files || !selectedPetId}
        >
          {isUploading ? (
            <>
              <Upload className="mr-2 h-4 w-4 animate-spin" />
              {t('modals.post.uploading')}
            </>
          ) : (
            t('modals.post.post')
          )}
        </Button>
      </form>
      
      {/* ✅ FASE 3: Upgrade Modal */}
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        reason={upgradeReason}
        currentTier={subscription?.data?.subscription_tier || 'free'}
      />
    </div>
  );
};

export default CreatePost;
